import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {Container, Row, Col, Form, Button, Card, FormGroup, FormCheck} from "react-bootstrap";
import axios from "axios";
import { storageKeys } from "../../constants/storageKeys";

function ConsentPage() {
  const navigate = useNavigate();

  const history = useSelector((state) => state.history);
  const messages = useSelector((state) => state.messages);

  const [uploading, setUploading] = useState(false);
  const [consentChecked, setConsentChecked] = useState(true);
  const [surveyChecked, setSurveyChecked] = useState(true);

  // Redirect to home page if history is empty
  useEffect(() => {
    if (!history?.entries?.length) {
      navigate(`/`);
    }
  }, [history, navigate]);

  // Async function to handle form submission
  /**
   * Handles the submission of the consent form.
   * - If the user has given consent, saves the session data.
   * - If the user has opted to take the survey, navigates to the survey page.
   * - Otherwise, navigates back to the home page.
   */
  const handleSubmit = async () => {
    // If consent is checked, save the session data
    if (consentChecked) {
      await saveSession();
    }

    // If survey is checked, navigate to the survey page; otherwise, go to home
    if (surveyChecked) {
      navigate(`/survey`, { replace: true }); 
    } else {
      navigate("/", { replace: true });
    }
  };

  /**
   * Saves the current session data (history, messages, and localStorage) to the server.
   * - Retrieves the session ID from localStorage.
   * - Sets the uploading state to true to indicate upload in progress.
   * - Constructs a sessionState object containing history, messages, and a shallow copy of localStorage.
   * - Packages the sessionState as a JSON Blob and appends it to a FormData object.
   * - Sends the FormData to the backend endpoint for session uploads.
   * - Handles errors and resets the uploading state when done.
   *
   */
  const saveSession = async () => {
    // Retrieve the session ID from localStorage (if available)
    const sessionID = localStorage.getItem(storageKeys.SESSION_ID);

    // Indicate that uploading is in progress
    setUploading(true);

    try {
      // Construct the session state object to be saved
      const sessionState = {
        history,
        messages,
        localStorage: { ...localStorage }, // Shallow copy of localStorage
      };

      // Create a new FormData object for the upload
      const formData = new FormData();

      // Append the sessionState as a JSON Blob to the FormData
      formData.append(
        "sessionState",
        new Blob([JSON.stringify(sessionState)], { type: "application/json" }),
        `session_state_${sessionID || "unknown"}.json`
      );

      // Send the FormData to the backend endpoint for session uploads
      await axios.post(
        `${process.env.REACT_APP_PROXY}/upload/session`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      console.log("Session data saved successfully.");
    } catch (error) {
      console.error("Upload failed:", error);
    } finally {
      // Reset uploading state regardless of success or failure
      setUploading(false);
    }
  };

  return (
    <div className="bg-gray d-flex justify-content-center align-items-center vh-100">
      <Container>
        <Row className="justify-content-center">
          <Col xs={12} sm={8} md={6}>
            <Card className="shadow p-4">
              <Card.Body>
                <h4 className="mb-4">Thank you for using BPMN-Chatbot</h4>
                <Form className="mt-4">
                  <FormGroup controlId="formConsent" className="mb-0">
                    <FormCheck
                      type="checkbox"
                      label={
                        <span style={{ fontSize: "1.2rem" }}>
                          I consent to share the data from my session with
                          BPMN-Chatbot for research purposes.
                        </span>
                      }
                      onChange={() => setConsentChecked(!consentChecked)}
                      checked={consentChecked}
                    />
                  </FormGroup>

                  <FormGroup controlId="formSurvey" className="mb-0">
                    <FormCheck
                      type="checkbox"
                      label={
                        <span style={{ fontSize: "1.2rem" }}>
                          I would like to participate in an upcoming survey on
                          the BPMN-Chatbot
                        </span>
                      }
                      onChange={() => setSurveyChecked(!surveyChecked)}
                      checked={surveyChecked}
                    />
                  </FormGroup>

                  <div className="text-center mt-4">
                    <Button
                      variant="dark"
                      className="px-5"
                      onClick={handleSubmit}
                      disabled={uploading}
                    >
                      {uploading ? "Uploading..." : "Submit"}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default ConsentPage;
